import './style.scss';

import localforage from 'localforage';
import { PropsWithChildren, useEffect, useRef } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';

import { useProjectsData } from '@/entities/projects-data';

import { MM_MAIN_ELEMENT_ID } from 'shared/consts/dom';
import { ROUTE_PATHS } from 'shared/consts/routes';
import { useTypedSelector } from 'store';
import { LocalStorage } from 'tools';
import { CopyPopup } from 'ui/CopyPopup/CopyPopup';
import { EExchange, ExchangeRouteBasePath } from 'web3';

import { ApplicationContent } from '../ApplicationContent';
import { EasterEgg } from '../easter-egg';
import { Header } from '../Header/Header';
import { Sidebar } from '../Sidebar/Sidebar';

export const ApplicationLayout = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const location = useLocation();
  const userIsAuthenticated = useTypedSelector(store => Boolean(store.auth.jwt));
  const user = useTypedSelector(store => store.auth.user);
  const isUser = useTypedSelector(store => store.auth.isUser);
  const projects = useTypedSelector(store => store.projects.projects);

  useEffect(() => {
    const clearLegacyProjectsCacheForWeek = async () => {
      const cleanupTtlMs = 7 * 24 * 60 * 60 * 1000;
      const cleanupKey = `${window?.location?.origin ?? 'mm-dash'}/projects-cache-cleanup-until`;
      const now = Date.now();
      const storedUntil = LocalStorage.get<number>(cleanupKey);
      const cleanupUntil =
        typeof storedUntil === 'number' && Number.isFinite(storedUntil)
          ? storedUntil
          : now + cleanupTtlMs;

      if (!storedUntil || storedUntil !== cleanupUntil) {
        LocalStorage.set(cleanupKey, cleanupUntil);
      }

      if (now > cleanupUntil) return;

      try {
        const storage = localforage.createInstance({ name: 'projects-cache', storeName: 'data' });
        await storage.clear();
      } catch (error) {
        console.error('[ApplicationLayout] Failed to clear legacy projects cache', error);
      }
    };

    void clearLegacyProjectsCacheForWeek();
  }, []);

  useEffect(() => {
    const cleanupArbitrageCacheDb = async () => {
      const cleanupTtlMs = 14 * 24 * 60 * 60 * 1000;
      const cleanupKey = `${window?.location?.origin ?? 'mm-dash'}/arbitrage-configs-db-cleanup-until`;
      const now = Date.now();
      const storedUntil = LocalStorage.get<number>(cleanupKey);
      const cleanupUntil =
        typeof storedUntil === 'number' && Number.isFinite(storedUntil)
          ? storedUntil
          : now + cleanupTtlMs;

      if (!storedUntil || storedUntil !== cleanupUntil) {
        LocalStorage.set(cleanupKey, cleanupUntil);
      }

      if (now > cleanupUntil) return;

      const dbNames = ['arbitrage-configs-cache', 'arbitrage-config-cache'];

      for (const name of dbNames) {
        try {
          await localforage.dropInstance({ name });
        } catch (error) {
          console.error('[ApplicationLayout] Failed to drop arbitrage cache DB', { name, error });
        }

        if (typeof indexedDB !== 'undefined') {
          try {
            await new Promise<void>((resolve, reject) => {
              const request = indexedDB.deleteDatabase(name);
              request.onsuccess = () => resolve();
              request.onerror = () => reject(request.error);
              request.onblocked = () => resolve();
            });
          } catch (error) {
            console.error('[ApplicationLayout] Failed to delete arbitrage cache DB', {
              name,
              error,
            });
          }
        }
      }
    };

    void cleanupArbitrageCacheDb();
  }, []);

  useEffect(() => {
    const cleanupBalancerCacheDb = async () => {
      const cleanupTtlMs = 14 * 24 * 60 * 60 * 1000;
      const cleanupKey = `${window?.location?.origin ?? 'mm-dash'}/balancer-tasks-db-cleanup-until`;
      const now = Date.now();
      const storedUntil = LocalStorage.get<number>(cleanupKey);
      const cleanupUntil =
        typeof storedUntil === 'number' && Number.isFinite(storedUntil)
          ? storedUntil
          : now + cleanupTtlMs;

      if (!storedUntil || storedUntil !== cleanupUntil) {
        LocalStorage.set(cleanupKey, cleanupUntil);
      }

      if (now > cleanupUntil) return;

      const dbNames = ['wallet-balancer-tasks-cache', 'wallet-balancer-task-cache'];

      for (const name of dbNames) {
        try {
          await localforage.dropInstance({ name });
        } catch (error) {
          console.error('[ApplicationLayout] Failed to drop balancer cache DB', { name, error });
        }

        if (typeof indexedDB !== 'undefined') {
          try {
            await new Promise<void>((resolve, reject) => {
              const request = indexedDB.deleteDatabase(name);
              request.onsuccess = () => resolve();
              request.onerror = () => reject(request.error);
              request.onblocked = () => resolve();
            });
          } catch (error) {
            console.error('[ApplicationLayout] Failed to delete balancer cache DB', {
              name,
              error,
            });
          }
        }
      }
    };

    void cleanupBalancerCacheDb();
  }, []);

  // Track if redirect has been done to prevent multiple redirects
  const hasRedirectedRef = useRef(false);

  // Use the new cached projects data hook
  useProjectsData({ enabled: userIsAuthenticated && !!user });

  // Handle redirect to first project pair (extracted from old thunk)
  useEffect(() => {
    // Only redirect once, when user is authenticated, on root path, and projects are loaded
    if (
      hasRedirectedRef.current ||
      !user ||
      !isUser ||
      location.pathname !== '/' ||
      !projects ||
      projects.length === 0
    ) {
      return;
    }

    const firstProject = projects[0];
    const pairs = [
      ...(firstProject.dexPairs ?? []),
      ...(firstProject.cexPairs ?? []),
      ...(firstProject.solPairs ?? []),
    ].sort((a, b) => new Date(a.created_at).valueOf() - new Date(b.created_at).valueOf());

    if (pairs.length > 0) {
      const firstPair = pairs[0];
      let pairPath: string | undefined;

      if ('cex' in firstPair) {
        pairPath = `${ExchangeRouteBasePath[EExchange.cex]}/${firstPair.id}`;
      } else if ('dex' in firstPair) {
        if (firstPair.network === 'solana') {
          pairPath = `${ExchangeRouteBasePath[EExchange.sol]}/${firstPair.id}`;
        } else {
          pairPath = `${ExchangeRouteBasePath[EExchange.dex]}/${firstPair.id}`;
        }
      }

      if (pairPath) {
        hasRedirectedRef.current = true;
        navigate(
          generatePath(ROUTE_PATHS.project, {
            id: `${firstProject.id}${pairPath}`,
          }),
        );
      }
    }
  }, [user, isUser, location.pathname, projects, navigate]);

  return (
    <div className="mm-application-layout">
      <Sidebar />
      <div className="mm-application-layout__main__holder">
        <section id={MM_MAIN_ELEMENT_ID} className="mm-application-layout__main scrollable">
          <div className="mm-application-layout__main__container">
            <Header />
            <ApplicationContent>{children}</ApplicationContent>
          </div>
        </section>
        <CopyPopup />
        <EasterEgg />
      </div>
    </div>
  );
};
